import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import classNames from "classnames";
import Switch from "@mui/material/Switch";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import styles from "./styles.module.scss";
import Spinner from "../../components/common/Spinner";
import OrgAvatar from "./OrgAvatar";
import ToggleConfirmModal, { TToggleAction } from "./ToggleConfirmModal";
import EditIncludedOrgsModal from "./EditIncludedOrgsModal";
import { applyIncludedChanges } from "./helpers";
import {
  getAppProp,
  getAppProps,
  setAppPropEnabled,
  updateAppPropValue,
} from "../../setup/api/services/appProps";
import { APP_PROP_NAMES } from "../../setup/api/services/appProps/constants";
import { IAppProp } from "../../setup/api/services/appProps/models";
import { getAllOrganizations } from "../../setup/api/services/organization";
import { IOrganizations } from "../../setup/types/Organisation";
import {
  invalidateAppPropsCache,
  parseAppPropBoolean,
  parseAppPropList,
} from "../../hooks/useAppProps";

const PROP_NAMES = [
  APP_PROP_NAMES.AUTO_CASHOUT_RUN_SCHEDULED,
  APP_PROP_NAMES.AUTO_CASHOUT_INCLUDE_ORG_CODES,
];

// Large enough to list every organization in the edit modal in one request.
const ORGANIZATIONS_PAGE_SIZE = 1000;

/**
 * Auto Cashout settings (WAF-3236): start or stop the scheduled auto-cashout
 * job and choose which organizations it pays out for. Both are app props:
 * AUTO_CASHOUT_RUN_SCHEDULED ("true"/"false") and
 * AUTO_CASHOUT_INCLUDE_ORG_CODES (comma-separated organization codes).
 */
const AutoCashoutSettings = () => {
  const { t } = useTranslation();
  const [runProp, setRunProp] = useState<IAppProp | null>(null);
  const [includeProp, setIncludeProp] = useState<IAppProp | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [organizations, setOrganizations] = useState<IOrganizations[]>([]);
  const [organizationsLoadFailed, setOrganizationsLoadFailed] = useState(false);
  const [toggleAction, setToggleAction] = useState<TToggleAction | null>(null);
  const [isToggling, setIsToggling] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchProps = useCallback(async () => {
    try {
      const response = await getAppProps(0, 20, PROP_NAMES);
      const appProps = response?.data?.content ?? [];
      const findProp = (name: string) =>
        appProps.find((prop) => prop.name === name) ?? null;
      setRunProp(findProp(APP_PROP_NAMES.AUTO_CASHOUT_RUN_SCHEDULED));
      setIncludeProp(findProp(APP_PROP_NAMES.AUTO_CASHOUT_INCLUDE_ORG_CODES));
      setLoadFailed(false);
    } catch (error) {
      console.error(error);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await getAllOrganizations(0, ORGANIZATIONS_PAGE_SIZE);
      setOrganizations(response?.data?.content ?? []);
      setOrganizationsLoadFailed(false);
    } catch (error) {
      console.error(error);
      setOrganizationsLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    fetchProps();
    fetchOrganizations();
  }, [fetchProps, fetchOrganizations]);

  // A disabled prop is ignored by the backend, so it only counts as running
  // when it's both enabled and "true".
  const isRunning = !!runProp?.enabled && parseAppPropBoolean(runProp?.value);

  const includedCodes = useMemo(
    () => parseAppPropList(includeProp?.value),
    [includeProp]
  );

  const logoByCode = useMemo(
    () => new Map(organizations.map((org) => [org.code, org.logoUrl] as const)),
    [organizations]
  );

  const afterWrite = async () => {
    invalidateAppPropsCache();
    await fetchProps();
  };

  const handleConfirmToggle = async () => {
    if (!runProp || !toggleAction) return;
    const isStart = toggleAction === "start";
    setIsToggling(true);
    try {
      await updateAppPropValue(runProp.id, { value: String(isStart) });
      if (isStart && !runProp.enabled) {
        await setAppPropEnabled(runProp.id, true);
      }
      setToggleAction(null);
      toast.success(
        isStart
          ? t("lbl_auto_cashout_settings_started_toast")
          : t("lbl_auto_cashout_settings_stopped_toast")
      );
      await afterWrite();
    } catch (error) {
      console.error(error);
      toast.error(t("lbl_auto_cashout_settings_toggle_failed"));
      await afterWrite();
    } finally {
      setIsToggling(false);
    }
  };

  // Rejections propagate so the modal stays open and shows its error.
  const handleSaveIncluded = async (added: string[], removed: string[]) => {
    if (!includeProp) return;
    const latest = await getAppProp(includeProp.id);
    const nextCodes = applyIncludedChanges(
      parseAppPropList(latest?.data?.value),
      added,
      removed
    );
    await updateAppPropValue(includeProp.id, { value: nextCodes.join(",") });
    setIsEditOpen(false);
    toast.success(t("lbl_auto_cashout_settings_saved_toast"));
    await afterWrite();
  };

  const renderStatusCard = () => (
    <div
      className={classNames(styles.card, styles.statusCard, {
        [styles.statusCardRunning]: isRunning,
      })}>
      <span
        className={classNames(styles.statusIcon, {
          [styles.statusIconRunning]: isRunning,
        })}>
        <AutorenewIcon />
      </span>
      <div className={styles.statusText}>
        <div className={styles.titleRow}>
          <h3 className={styles.cardTitle}>
            {t("lbl_auto_cashout_settings_status_title")}
          </h3>
          <span
            className={classNames(styles.statusBadge, {
              [styles.statusBadgeRunning]: isRunning,
            })}>
            <span className={styles.statusDot} />
            {isRunning
              ? t("lbl_auto_cashout_settings_running")
              : t("lbl_auto_cashout_settings_stopped")}
          </span>
        </div>
        <p className={styles.cardSubtitle}>
          {runProp
            ? isRunning
              ? t("lbl_auto_cashout_settings_running_desc")
              : t("lbl_auto_cashout_settings_stopped_desc")
            : t("lbl_auto_cashout_settings_prop_missing")}
        </p>
      </div>
      <Switch
        checked={isRunning}
        disabled={!runProp || isToggling}
        onChange={(_, checked) => setToggleAction(checked ? "start" : "stop")}
        inputProps={{
          "aria-label": t("lbl_auto_cashout_settings_status_title"),
        }}
      />
    </div>
  );

  const renderIncludedCard = () => (
    <div className={classNames(styles.card, styles.includedCard)}>
      <div className={styles.includedHeader}>
        <div className={styles.includedHeaderText}>
          <div className={styles.titleRow}>
            <h3 className={styles.cardTitle}>
              {t("lbl_auto_cashout_settings_included_title")}
            </h3>
            <span className={styles.countBadge}>{includedCodes.length}</span>
          </div>
          <p className={styles.cardSubtitle}>
            {includeProp
              ? t("lbl_auto_cashout_settings_included_desc")
              : t("lbl_auto_cashout_settings_prop_missing")}
          </p>
          {includeProp && !includeProp.enabled && (
            <p className={styles.warning}>
              {t("lbl_auto_cashout_settings_list_disabled")}
            </p>
          )}
        </div>
        <button
          type="button"
          className={styles.editButton}
          disabled={!includeProp}
          onClick={() => setIsEditOpen(true)}>
          {t("lbl_auto_cashout_settings_edit")}
        </button>
      </div>
      {includedCodes.length ? (
        includedCodes.map((code) => (
          <div key={code} className={styles.orgRow}>
            <OrgAvatar code={code} logoUrl={logoByCode.get(code)} />
            <span className={styles.orgCode}>{code}</span>
          </div>
        ))
      ) : (
        <p className={styles.emptyState}>
          {t("lbl_auto_cashout_settings_included_empty")}
        </p>
      )}
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.centered}>
          <Spinner />
        </div>
      );
    }
    if (loadFailed) {
      return (
        <div className={classNames(styles.card, styles.centered)}>
          <p className={styles.cardSubtitle}>
            {t("lbl_auto_cashout_settings_load_failed")}
          </p>
          <button
            type="button"
            className={styles.editButton}
            onClick={() => {
              setIsLoading(true);
              fetchProps();
            }}>
            {t("lbl_auto_cashout_settings_retry")}
          </button>
        </div>
      );
    }
    return (
      <>
        {renderStatusCard()}
        {renderIncludedCard()}
      </>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            {t("lbl_auto_cashout_settings_title")}
          </h1>
          <p className={styles.pageSubtitle}>
            {t("lbl_auto_cashout_settings_subtitle")}
          </p>
        </div>
        {renderContent()}
      </div>

      <ToggleConfirmModal
        action={toggleAction}
        saving={isToggling}
        handleClose={() => setToggleAction(null)}
        handleConfirm={handleConfirmToggle}
      />

      <EditIncludedOrgsModal
        open={isEditOpen}
        includedCodes={includedCodes}
        organizations={organizations}
        organizationsLoadFailed={organizationsLoadFailed}
        handleClose={() => setIsEditOpen(false)}
        handleSave={handleSaveIncluded}
      />
    </div>
  );
};

export default AutoCashoutSettings;
